import axios from 'axios';
import { REST_API } from 'src/constants/DefaultValues';
import { sefiAuthHeaders } from './sefiAuth';
import { isRagDocumentFile, isVideoFile } from './fileHelpers';
import { VIDEO_PROCESSING_STEPS } from '../constants';

/** Upload chat documents for RAG indexing before /ai/chat. */
export const uploadChatDocuments = async (
  files,
  { sessionId, userId, newSession, uploadType, signal, projectId }
) => {
  const docs = (files || []).filter((f) => f?.file && isRagDocumentFile(f));
  if (!docs.length) {
    return { sessionId: sessionId || null, results: [] };
  }

  let resolvedSessionId = sessionId || null;
  const results = [];

  for (const item of docs) {
    const form = new FormData();
    form.append('file', item.file, item.name || item.file.name);
    if (resolvedSessionId) form.append('session_id', String(resolvedSessionId));
    if (userId) form.append('user_id', String(userId));
    if (newSession) form.append('new_session', 'true');
    if (uploadType) form.append('upload_type', uploadType);
    if (projectId) form.append('project_id', String(projectId));

    const response = await axios.post(`${REST_API}ai/documents/upload`, form, {
      headers: {
        ...sefiAuthHeaders(),
      },
      timeout: 300000,
      maxBodyLength: Infinity,
      signal,
    });

    const data = response?.data || {};
    if (!data.success && data.error) {
      throw new Error(data.error);
    }
    if (data.session_id && !resolvedSessionId) {
      resolvedSessionId = data.session_id;
    }
    results.push(data);
  }

  return { sessionId: resolvedSessionId, results };
};

/** Upload chat videos for recognition — returns summary payload (no /ai/chat/stream). */
export const uploadChatVideos = async (
  files,
  { sessionId, userId, newSession, userQuestion, onProgress, signal, projectId }
) => {
  const videos = (files || []).filter((f) => f?.file && isVideoFile(f));
  if (!videos.length) {
    return { sessionId: sessionId || null, results: [] };
  }

  let resolvedSessionId = sessionId || null;
  const results = [];
  let stepIndex = 0;
  if (onProgress) onProgress(VIDEO_PROCESSING_STEPS[0]);
  const stepTimer = setInterval(() => {
    stepIndex = Math.min(stepIndex + 1, VIDEO_PROCESSING_STEPS.length - 1);
    if (onProgress) onProgress(VIDEO_PROCESSING_STEPS[stepIndex]);
  }, 4500);

  try {
    for (const item of videos) {
      const form = new FormData();
      form.append('file', item.file, item.name || item.file.name);
      if (resolvedSessionId) form.append('session_id', String(resolvedSessionId));
      if (userId) form.append('user_id', String(userId));
      if (newSession) form.append('new_session', 'true');
      form.append('upload_type', 'document');
      if (userQuestion && String(userQuestion).trim()) {
        form.append('user_question', String(userQuestion).trim());
      }
      if (projectId) form.append('project_id', String(projectId));

      const response = await axios.post(`${REST_API}ai/documents/upload`, form, {
        headers: {
          ...sefiAuthHeaders(),
        },
        timeout: 900000,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        signal,
      });

      const data = response?.data || {};
      if (!data.success && (data.error || data.message)) {
        throw new Error(data.error || data.message || 'Video upload failed');
      }
      if (data.session_id && !resolvedSessionId) {
        resolvedSessionId = data.session_id;
      }
      results.push(data);
    }
  } finally {
    clearInterval(stepTimer);
  }

  return { sessionId: resolvedSessionId, results };
};
